import React, { useState, useEffect } from "react";
import { X, Check } from "lucide-react";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import toast from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";
import { useNavigate } from "react-router-dom"; // Added useNavigate

const DurationSelectionModal = ({
    isOpen,
    onClose,
    onProceed,
    listingId,
    isRenewal = false,
    currentPlan = null,
    planType = 'HOUSING'
}) => {
    const [selectedDuration, setSelectedDuration] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingPlans, setFetchingPlans] = useState(false);
    const [cashfree, setCashfree] = useState(null);
    const navigate = useNavigate();

    // Initialize Cashfree
    const initializeCashfree = async () => {
        try {
            const mode = process.env.REACT_APP_CASHFREE_MODE || "sandbox";
            const cf = await load({ mode });
            setCashfree(cf);
        } catch (err) {
            console.error("Failed to load Cashfree SDK:", err);
        }
    };

    useEffect(() => {
        initializeCashfree();
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchPricingPlans();
        }
    }, [isOpen, planType]);

    const fetchPricingPlans = async () => {
        try {
            setFetchingPlans(true);
            const response = await axiosInstancev1.get(`/properties/plans?type=${planType}`);
            if (response.data.success || response.data.plans) {
                const rawPlans = response.data.plans || [];
                // Format plans for the UI
                const formattedPlans = rawPlans.map(plan => {
                    let label = "";
                    let discount = "";
                    if (plan.duration_months === 3) label = "Quarterly";
                    else if (plan.duration_months === 6) { label = "Half Yearly"; discount = "Save 8%"; }
                    else if (plan.duration_months === 9) { label = "9 Months"; discount = "Save 11%"; }
                    else if (plan.duration_months === 12) { label = "Annual"; discount = "Save 16%"; }
                    else label = `${plan.duration_months} Months`;

                    return {
                        ...plan,
                        label: label,
                        discount: discount
                    };
                });
                setPlans(formattedPlans);
            }
        } catch (error) {
            console.error("Error fetching pricing plans:", error);
            toast.error("Failed to load pricing plans");
        } finally {
            setFetchingPlans(false);
        }
    };

    const handleProceedToPayment = async () => {
        if (!selectedDuration) {
            toast.error("Please select a duration");
            return;
        }

        if (!cashfree) {
            toast.error("Payment system is initializing, please wait...");
            return;
        }

        setLoading(true);
        try {
            // Determine return URL based on current path (builder or agent)
            const isBuilder = window.location.pathname.startsWith("/builder");
            let returnPath = "/agent/housingpost";
            if (isBuilder) {
                returnPath = window.location.pathname.includes("plot") ? "/builder/plot" : "/builder/dashboard";
            }

            const response = await axiosInstancev1.post(
                `/properties/${listingId}/payment/create-order`,
                {
                    durationMonths: selectedDuration,
                    returnUrl: window.location.origin + returnPath
                }
            );

            if (response.data.activated) {
                toast.success("Payment verified! Listing activated.");
                onClose();
                window.location.reload();
                return;
            }

            if (response.data.success && response.data.paymentSessionId) {
                const checkoutOptions = {
                    paymentSessionId: response.data.paymentSessionId,
                    redirectTarget: "_modal",
                };

                cashfree.checkout(checkoutOptions).then(async (result) => {
                    if (result?.error) {
                        toast.error(result.error.message || "Payment failed to start");
                        console.error("Cashfree error:", result.error);
                    } else {
                        try {
                            const verifyRes = await axiosInstancev1.post(`/properties/${listingId}/payment/verify`, {
                                merchantOrderId: response.data.merchantOrderId
                            });
                            if (verifyRes.data.success) {
                                toast.success("Payment Successful! Listing activated.");
                                onClose();
                                window.location.reload();
                            }
                        } catch (vErr) {
                            console.error("Verification error:", vErr);
                        }
                    }
                });
            } else {
                toast.error("Failed to initialize payment session");
            }
        } catch (error) {
            console.error("Error creating payment order:", error);
            toast.error(error.response?.data?.message || "Failed to create payment order");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <X size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-white mb-1">Select Plan Duration</h2>
                    <p className="text-blue-100 text-sm">Choose the best plan for your needs</p>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                    {fetchingPlans ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : plans.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No plans available</div>
                    ) : (
                        plans.map((plan) => (
                            <div
                                key={plan.duration_months}
                                onClick={() => setSelectedDuration(plan.duration_months)}
                                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 flex justify-between items-center
                                    ${selectedDuration === plan.duration_months
                                        ? 'border-blue-600 bg-blue-50'
                                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                    }
                                `}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                        ${selectedDuration === plan.duration_months ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}
                                    `}>
                                        {selectedDuration === plan.duration_months && <Check size={12} className="text-white" />}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">{plan.label} ({plan.duration_months} Months)</h3>
                                        {plan.discount && <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-0.5 rounded-full">{plan.discount}</span>}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0 ml-2">
                                    <span className="block text-lg font-bold text-slate-900">₹{parseFloat(plan.price).toLocaleString()}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-slate-50">
                    <div className="flex justify-between items-center mb-4 text-slate-700">
                        <span className="font-medium">Total Amount:</span>
                        <span className="text-2xl font-bold text-blue-700">
                            ₹{plans.find(p => p.duration_months === selectedDuration)?.price.toLocaleString() || "0"}
                        </span>
                    </div>
                    <button
                        onClick={handleProceedToPayment}
                        disabled={loading || !selectedDuration}
                        className={`w-full py-3 rounded-xl font-semibold shadow-lg shadow-blue-500/30 transition-all active:scale-95
                             ${loading || !selectedDuration
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                            }`}
                    >
                        {loading ? "Processing..." : "Proceed to Pay"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DurationSelectionModal;